<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { EditorState } from '@common/Common'
    import
        {
            CreateCostSubtotalEventPayload,
            RenameCostSubtotalEventPayload
        } from '@Quest/Costs/Components/CostSubtotalEditor'
    import type { CostSubtotalEditorViewModel } from '@Quest/Costs/Components/CostSubtotalEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: CostSubtotalEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-subtotal', payload: CreateCostSubtotalEventPayload): void
        (e: 'rename-subtotal', payload: RenameCostSubtotalEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const confirmButtonLabel = computed(() =>
        vm.value.state === EditorState.Creator ? 'Create' : 'Update'
    )

    const modalTitle = computed(() =>
        vm.value.state === EditorState.Creator ? 'Create subtotal' : 'Edit subtotal'
    )

    // writable computed (replaces getter + setter)

    const name = computed<string | null>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        if (vm.value.state === EditorState.Creator)
        {
            emit(
                'create-subtotal',
                new CreateCostSubtotalEventPayload(name.value)
            )
        }
        else if (vm.value.state === EditorState.Editor)
        {
            emit(
                'rename-subtotal',
                new RenameCostSubtotalEventPayload(vm.value.id, name.value)
            )
        }
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form id="cost-subtotal-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required maxlength="100" minlength="2" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="cost-subtotal-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>